import Image from "next/image"
import { useTranslations } from "next-intl"

import { NeloMarkIcon } from "@/components/ui/icons"

const SIZES = {
  sm: { text: "text-body-sm", gap: "gap-50" },
  md: { text: "text-heading-md", gap: "gap-100" },
  lg: { text: "text-display-sm", gap: "gap-150" },
} as const

export const CLAIM_VARIANTS = ["cero", "hoy"] as const

type ClaimVariant = (typeof CLAIM_VARIANTS)[number]

// The ring stands in for the numeral zero in `0%` and nowhere else — Connor's rule,
// 2026-08-05. The `%` supplies the numeric context that makes the shape read as a
// zero; without it the eye takes two arcs after a `$` as parentheses. Every other
// claim is set in type.
const RING_HEIGHT = "0.9em"

// Sized against the cap height of the type it sits in rather than a fixed pixel
// value, so one lockup works from a 28px badge to a hero without a second variant.
const Glyph = ({ src, ratio, height }: { src: string; ratio: number; height: string }) => (
  <Image src={src} alt="" width={130} height={128} aria-hidden loading="eager" style={{ height, width: `calc(${height} * ${ratio})` }} />
)

export const ClaimLockup = ({ variant, size = "md", onBrand = false, className }: { variant: ClaimVariant; size?: keyof typeof SIZES; onBrand?: boolean; className?: string }) => {
  const t = useTranslations("marketplace.bnpl.claims")
  const { text, gap } = SIZES[size]
  const ring = onBrand ? <NeloMarkIcon style={{ height: RING_HEIGHT, width: RING_HEIGHT }} /> : <Glyph src="/images/nelo-o-mark.svg" ratio={130 / 128} height={RING_HEIGHT} />

  const device = {
    cero: (
      <span className="flex items-center">
        {ring}
        <span>%</span>
      </span>
    ),
    hoy: (
      <>
        <span>{t("hoy.word")}</span>
        <span>$0</span>
      </>
    ),
  }[variant]

  const trailing = variant === "cero" ? t("cero.word") : null

  return (
    <p
      role="img"
      aria-label={t(`${variant}.aria`)}
      className={`font-display ${text} ${gap} ${onBrand ? "text-fg-inverted" : ""} flex items-center leading-none font-extrabold tracking-tight ${className ?? ""}`}
    >
      <span aria-hidden className={`flex items-center ${gap}`}>
        {device}
      </span>
      {trailing ? (
        <span aria-hidden className="font-normal">
          {trailing}
        </span>
      ) : null}
    </p>
  )
}
